/**
 * LO Prospect Pipeline Service (Partner Pricing Preview)
 *
 * CRUD + stage management for `lo_prospects`, pricing-run reads/reset for
 * `lo_prospect_pricing_runs`, the 3-table NMLS duplicate preflight, and the
 * transfer-into-recruits flow. Mirrors recruitmentService conventions.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  loProspectFromRows,
  loProspectUpdateToRow,
  normalizeLoProspectStage,
  type LoProspect,
  type LoProspectEngagementSummary,
  type LoProspectPricingFailure,
  type LoProspectRow,
  type LoProspectPricingRunRow,
  type LoProspectStage,
  type LoProspectEditableFields
} from '@/types/lo-prospect';
import { recruitToDatabase } from '@/types/recruitment';
import type { RecruitmentFormValues } from '@/components/recruitment/validations';

const log = createComponentLogger('LoProspectService', 'lo-prospects');

// lo_prospect_pricing_runs has TWO FKs to lo_prospects (the single-column one
// and the composite same-org one), so the embed must name a relationship or
// PostgREST errors with "more than one relationship found".
const PROSPECT_SELECT = `
  *,
  lo_prospect_pricing_runs!lo_prospect_pricing_runs_lo_prospect_id_fkey(*)
`;

/** Normalize an NMLS to a stable comparison key: trimmed, digits only. */
export function normalizeNmls(value: string): string {
  return (value ?? '').replace(/[^0-9]/g, '');
}

interface LoProspectWithRuns extends LoProspectRow {
  lo_prospect_pricing_runs: LoProspectPricingRunRow[] | null;
}

interface LoProspectEngagementSummaryRow {
  lo_prospect_id: string;
  last_invite_opened_at: string | null;
  invite_opened_count: number | null;
  last_nmls_login_at: string | null;
  nmls_login_count: number | null;
}

interface LoProspectPricingFailureRow {
  attempt_id: string;
  attempted_at: string;
  detail: Record<string, unknown> | null;
}

const PRICING_FAILURE_CATEGORIES = new Set<LoProspectPricingFailure['category']>([
  'scenario_validation',
  'upstream_auth',
  'upstream_rate_limit',
  'timeout',
  'network',
  'upstream_unavailable',
  'invalid_response',
  'legacy'
]);

function pricingFailureFromRow(row: LoProspectPricingFailureRow): LoProspectPricingFailure {
  const detail = row.detail && typeof row.detail === 'object' ? row.detail : {};
  const rawCategory = typeof detail.category === 'string' ? detail.category : 'legacy';
  const category = PRICING_FAILURE_CATEGORIES.has(rawCategory as LoProspectPricingFailure['category'])
    ? rawCategory as LoProspectPricingFailure['category']
    : 'legacy';
  const issues = Array.isArray(detail.issues)
    ? detail.issues
      .filter((issue): issue is Record<string, unknown> => Boolean(issue) && typeof issue === 'object' && !Array.isArray(issue))
      .map((issue) => ({
        input: typeof issue.input === 'string' ? issue.input.slice(0, 100) : null,
        message: typeof issue.message === 'string' ? issue.message.slice(0, 280) : ''
      }))
      .filter((issue) => Boolean(issue.message))
      .slice(0, 12)
    : [];

  return {
    id: row.attempt_id,
    attemptedAt: row.attempted_at,
    category,
    httpStatus: typeof detail.httpStatus === 'number' ? detail.httpStatus : null,
    message: typeof detail.message === 'string' && detail.message.trim()
      ? detail.message.slice(0, 280)
      : 'Detailed failure information was not recorded for this attempt.',
    issues,
    retryable: detail.retryable === true,
    traceId: typeof detail.traceId === 'string' ? detail.traceId.slice(0, 180) : null,
    scenario: detail.scenario && typeof detail.scenario === 'object' && !Array.isArray(detail.scenario)
      ? detail.scenario as Record<string, unknown>
      : {},
    legacy: category === 'legacy' || detail.version !== 1
  };
}

function engagementSummaryFromRow(row: LoProspectEngagementSummaryRow): LoProspectEngagementSummary {
  return {
    lastInviteOpenedAt: row.last_invite_opened_at,
    inviteOpenedCount: row.invite_opened_count ?? 0,
    lastNmlsLoginAt: row.last_nmls_login_at,
    nmlsLoginCount: row.nmls_login_count ?? 0
  };
}

function engagementMap(rows: LoProspectEngagementSummaryRow[] | null | undefined): Map<string, LoProspectEngagementSummary> {
  const map = new Map<string, LoProspectEngagementSummary>();
  for (const row of rows ?? []) {
    map.set(row.lo_prospect_id, engagementSummaryFromRow(row));
  }
  return map;
}

async function getEngagementSummaries(
  organizationId: string,
  prospectId?: string
): Promise<Map<string, LoProspectEngagementSummary>> {
  const payload: { p_organization_id: string; p_prospect_id?: string } = { p_organization_id: organizationId };
  if (prospectId) {
    payload.p_prospect_id = prospectId;
  }
  const { data, error } = await supabase.rpc('lo_prospect_engagement_summary', payload);
  if (error) {
    throw error;
  }
  return engagementMap(data as LoProspectEngagementSummaryRow[] | null);
}

function mapRow(row: LoProspectWithRuns, engagement?: LoProspectEngagementSummary): LoProspect {
  return loProspectFromRows(row, row.lo_prospect_pricing_runs ?? [], engagement);
}

async function mapRowWithEngagement(row: LoProspectWithRuns): Promise<LoProspect> {
  const summaries = await getEngagementSummaries(row.organization_id, row.id);
  return mapRow(row, summaries.get(row.id));
}

async function getLoProspectRow(prospectId: string, organizationId?: string): Promise<LoProspectWithRuns | null> {
  let query = supabase
    .from('lo_prospects')
    .select(PROSPECT_SELECT)
    .eq('id', prospectId);
  if (organizationId) {
    query = query.eq('organization_id', organizationId);
  }
  const { data, error } = await query.single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    throw error;
  }
  return data as LoProspectWithRuns;
}

// ============================================================================
// Reads
// ============================================================================

export async function getLoProspects(organizationId: string): Promise<LoProspect[]> {
  const prospectsQuery = supabase
    .from('lo_prospects')
    .select(PROSPECT_SELECT)
    .eq('organization_id', organizationId)
    .order('last_active_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  const [prospectsResult, summaries] = await Promise.all([
    prospectsQuery,
    getEngagementSummaries(organizationId)
  ]);
  const { data, error } = prospectsResult;

  if (error) {
    throw error;
  }
  return (data as LoProspectWithRuns[]).map((row) => mapRow(row, summaries.get(row.id)));
}

export async function getLoProspect(prospectId: string, organizationId?: string): Promise<LoProspect | null> {
  if (organizationId) {
    const [row, summaries] = await Promise.all([
      getLoProspectRow(prospectId, organizationId),
      getEngagementSummaries(organizationId, prospectId)
    ]);
    return row ? mapRow(row, summaries.get(row.id)) : null;
  }

  const row = await getLoProspectRow(prospectId);
  if (!row) {
    return null;
  }
  const summaries = await getEngagementSummaries(row.organization_id, prospectId);
  return mapRow(row, summaries.get(row.id));
}

/** Safe, access-checked failures from the service-role-only OB attempt ledger.
 * The RPC strips request payloads and diagnostics before returning rows. */
export async function getLoProspectPricingFailures(
  organizationId: string,
  prospectId: string
): Promise<LoProspectPricingFailure[]> {
  const { data, error } = await supabase.rpc('lo_prospect_pricing_failure_attempts', {
    p_organization_id: organizationId,
    p_prospect_id: prospectId,
    p_limit: 20
  });
  if (error) {
    throw error;
  }
  return ((data ?? []) as LoProspectPricingFailureRow[]).map(pricingFailureFromRow);
}

/** Active org users available to assign as recruiters. */
export interface RecruiterOption {
  id: string;
  name: string;
}

export async function getProspectRecruiters(organizationId: string): Promise<RecruiterOption[]> {
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email')
    .eq('organization_id', organizationId)
    .is('deleted_at', null)
    .eq('employment_status', 'active')
    .order('full_name', { ascending: true });

  if (error) {
    throw error;
  }
  return (data as { id: string; full_name: string | null; email: string }[]).map((u) => ({
    id: u.id,
    name: u.full_name?.trim() || u.email
  }));
}

/**
 * GLOBAL-ONLY + UNCACHED read of the pricing-profiles flag, on purpose: the
 * pricing edge functions read this flag globally per invocation, while the
 * cached, per-user-override-aware featureFlagService (5-minute TTL) would let
 * the selector disagree with what pricing actually does — including keeping
 * the selector alive for minutes after a backend flag-off rollback.
 */
export async function getPricingProfilesUiEnabled(): Promise<boolean> {
  const { data, error } = await supabase
    .from('feature_flags')
    .select('enabled')
    .eq('flag_name', 'lo_prospect_pricing_profiles_v1')
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as { enabled?: unknown } | null)?.enabled === true;
}

/**
 * GLOBAL-ONLY + UNCACHED, matching the service-only resolver's rollout gate.
 * This controls whether the open drawer asks for sanitized managed-seat status;
 * the database and pricing functions remain the authorization boundaries.
 */
export async function getManagedUserPricingUiEnabled(): Promise<boolean> {
  const { data, error } = await supabase
    .from('feature_flags')
    .select('enabled')
    .eq('flag_name', 'lo_prospect_managed_user_pricing_v1')
    .maybeSingle();
  if (error) {
    throw error;
  }
  return (data as { enabled?: unknown } | null)?.enabled === true;
}

export type ManagedUserPricingStatus =
  | 'ready'
  | 'flag_off'
  | 'not_managed'
  | 'ambiguous'
  | 'not_ready'
  | 'error';

const MANAGED_USER_PRICING_STATUSES = new Set<ManagedUserPricingStatus>([
  'ready',
  'flag_off',
  'not_managed',
  'ambiguous',
  'not_ready',
  'error'
]);

/**
 * Browser-safe, access-checked status for one prospect. The RPC intentionally
 * returns no Manage User UUID or Optimal Blue identifiers.
 */
export async function getManagedUserPricingStatus(
  prospectId: string
): Promise<ManagedUserPricingStatus> {
  const { data, error } = await supabase.rpc(
    'lo_prospect_managed_user_pricing_status',
    { p_prospect_id: prospectId }
  );
  if (error) {
    throw error;
  }
  const status = (data as { status?: unknown } | null)?.status;
  return typeof status === 'string'
    && MANAGED_USER_PRICING_STATUSES.has(status as ManagedUserPricingStatus)
    ? status as ManagedUserPricingStatus
    : 'error';
}

/** A Pricing Profile a prospect can price under (labels only — the raw OB
 *  identifiers are not column-granted to browsers). */
export interface PricingProfileOption {
  id: string;
  label: string;
  isDefault: boolean;
}

export async function getPricingProfiles(organizationId: string): Promise<PricingProfileOption[]> {
  // Explicit column list on purpose: authenticated has column-level SELECT only
  // (id, organization_id, label, is_default, sort_order) — `select('*')` would 400.
  const { data, error } = await supabase
    .from('lo_prospect_pricing_profiles')
    .select('id, label, is_default, sort_order')
    .eq('organization_id', organizationId)
    .order('sort_order', { ascending: true })
    .order('label', { ascending: true });

  if (error) {
    throw error;
  }
  return (data as { id: string; label: string; is_default: boolean }[]).map((p) => ({
    id: p.id,
    label: p.label,
    isDefault: p.is_default === true
  }));
}

// ============================================================================
// Duplicate preflight (lo_prospects + recruits + users)
// ============================================================================

export type NmlsMatchSource = 'lo_prospect' | 'recruit' | 'user';

export interface NmlsMatch {
  source: NmlsMatchSource;
  id: string;
  name: string | null;
  stage?: LoProspectStage;
  status?: string;
  city?: string | null;
  state?: string | null;
}

/**
 * Server-side check across lo_prospects, recruits, and users (same org) for an
 * existing NMLS. Returns the first match or null. Uses the SECURITY-DEFINER RPC
 * so it can see records the caller can't read directly.
 */
export async function lookupNmls(
  organizationId: string,
  nmls: string
): Promise<NmlsMatch | null> {
  const normalized = normalizeNmls(nmls);
  if (!normalized) {
    return null;
  }
  const { data, error } = await supabase.rpc('lo_prospect_nmls_lookup', {
    p_organization_id: organizationId,
    p_nmls: normalized
  });
  if (error) {
    throw error;
  }
  const match = (data as NmlsMatch | null) ?? null;
  // This RPC result bypasses loProspectFromRows, so the legacy-stage
  // normalization has to happen here too.
  if (match?.stage) {
    return { ...match, stage: normalizeLoProspectStage(match.stage) };
  }
  return match;
}

// ============================================================================
// Invite / create
// ============================================================================

export interface InviteLoProspectInput {
  nmls: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  recruiterId?: string | null;
  /** Explicit non-null = a deliberately chosen non-default Pricing Profile.
   *  Undefined/null = follow the org default (the insert OMITS the column). */
  pricingProfileId?: string | null;
}

export type InviteResult =
  | { status: 'created'; prospect: LoProspect }
  | { status: 'duplicate'; match: NmlsMatch };

/**
 * Invite a prospect. Blocks duplicates across prospects/recruits/users. The
 * unique (organization_id, nmls) index is the race-safe backstop.
 */
export async function inviteLoProspect(
  organizationId: string,
  input: InviteLoProspectInput,
  userId: string
): Promise<InviteResult> {
  const nmls = normalizeNmls(input.nmls);
  if (!nmls) {
    throw new Error('An NMLS ID is required to invite a loan officer.');
  }
  // Contact fields are required to invite (service-level enforcement, not just the
  // modal). The full profile is collected later at Transfer-to-Recruiting.
  const firstName = input.firstName?.trim() || '';
  const lastName = input.lastName?.trim() || '';
  const email = input.email?.trim() || '';
  const phone = input.phone?.trim() || '';
  if (!firstName || !lastName || !email || !phone) {
    throw new Error('First name, last name, email and phone are required to invite a loan officer.');
  }

  const existing = await lookupNmls(organizationId, nmls);
  if (existing) {
    return { status: 'duplicate', match: existing };
  }

  // pricing_profile_id is included ONLY when a profile was deliberately chosen:
  // omitting the key keeps invites working before the profiles migration exists
  // (PostgREST rejects unknown columns) and the column default covers NULL after.
  const insertRow: Record<string, unknown> = {
    organization_id: organizationId,
    nmls,
    first_name: firstName,
    last_name: lastName,
    email,
    phone,
    recruiter_id: input.recruiterId || null,
    stage: 'invited' as LoProspectStage,
    created_by: userId,
    updated_by: userId
  };
  if (input.pricingProfileId) {
    insertRow.pricing_profile_id = input.pricingProfileId;
  }

  const { data, error } = await supabase
    .from('lo_prospects')
    .insert(insertRow)
    .select(PROSPECT_SELECT)
    .single();

  if (error) {
    // Unique-violation backstop if a concurrent insert won the race.
    if (error.code === '23505') {
      const dup = await lookupNmls(organizationId, nmls);
      if (dup) {
        return { status: 'duplicate', match: dup };
      }
    }
    throw error;
  }

  log.info('Invited LO prospect', { nmls });
  return { status: 'created', prospect: mapRow(data as LoProspectWithRuns) };
}

// ============================================================================
// Update / stage
// ============================================================================

export async function updateLoProspect(
  prospectId: string,
  patch: Partial<LoProspectEditableFields>,
  userId: string
): Promise<LoProspect> {
  const updateRow = loProspectUpdateToRow(patch, userId);
  if ('nmls' in updateRow && typeof updateRow.nmls === 'string') {
    updateRow.nmls = normalizeNmls(updateRow.nmls);
    // Re-run the 3-table duplicate guard when the NMLS actually changes, so an
    // edit can't smuggle in a number that already belongs to a recruit/user.
    const { data: current } = await supabase
      .from('lo_prospects')
      .select('organization_id, nmls')
      .eq('id', prospectId)
      .single();
    if (current && updateRow.nmls !== (current as { nmls: string }).nmls) {
      const match = await lookupNmls((current as { organization_id: string }).organization_id, updateRow.nmls);
      if (match && !(match.source === 'lo_prospect' && match.id === prospectId)) {
        throw new Error(`NMLS #${updateRow.nmls} is already on file (${match.source}). You can’t reuse it.`);
      }
    }
  }
  const { data, error } = await supabase
    .from('lo_prospects')
    .update(updateRow)
    .eq('id', prospectId)
    .select(PROSPECT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return mapRowWithEngagement(data as LoProspectWithRuns);
}

export async function setLoProspectStage(
  prospectId: string,
  stage: LoProspectStage,
  userId: string
): Promise<LoProspect> {
  const { data, error } = await supabase
    .from('lo_prospects')
    .update({ stage, updated_by: userId, updated_at: new Date().toISOString() })
    .eq('id', prospectId)
    .select(PROSPECT_SELECT)
    .single();

  if (error) {
    throw error;
  }
  return mapRowWithEngagement(data as LoProspectWithRuns);
}

/**
 * Archive the current pricing batch so the prospect has a fresh 5 runs.
 * Intentionally does NOT touch last_active_at — resetting is a recruiter action
 * and must not look like LO activity.
 */
export async function resetLoProspectRuns(prospectId: string): Promise<LoProspect> {
  // Server-side RPC (advisory-checked) archives the current batch and never
  // touches last_active_at. Recruiters cannot write pricing rows directly.
  const { error } = await supabase.rpc('lo_prospect_reset_runs', { p_prospect_id: prospectId });
  if (error) {
    throw error;
  }
  // Also clear any in-flight public-preview reservation so the reset truly frees
  // all 5 slots (reservations live in a separate table from runs).
  const { error: resErr } = await supabase.rpc('lo_prospect_expire_reservations', { p_prospect_id: prospectId });
  if (resErr) {
    throw resErr;
  }
  const refreshed = await getLoProspect(prospectId);
  if (!refreshed) {
    throw new Error('Prospect not found after reset.');
  }
  return refreshed;
}

// ============================================================================
// Public preview access + invite (recruiter actions)
// ============================================================================

export interface SendInviteResult {
  status: 'sent' | 'closed' | 'no_email' | 'email_failed' | 'disabled' | 'notification_disabled' | 'duplicate' | 'unsubscribed' | string;
  email?: string;
  expiresAt?: string;
  publicPreviewLive?: boolean;
  message?: string;
}

/**
 * Issue (or re-issue) a tokenized Partner Pricing Preview invite by email and
 * enable preview access. Token mint + email happen server-side (the HMAC secret
 * never reaches the client); a resend supersedes any prior active link.
 */
export async function sendPreviewInvite(prospectId: string): Promise<SendInviteResult> {
  const { data, error } = await supabase.functions.invoke('lo-prospect-invite', {
    body: { loProspectId: prospectId }
  });
  if (error) {
    throw error;
  }
  return data as SendInviteResult;
}

/**
 * Enable or disable the public preview for a prospect. Disable also revokes
 * active invite links and expires in-flight reservations (locks the LO out).
 */
export async function setPreviewAccess(
  prospectId: string,
  enabled: boolean,
  reason?: string
): Promise<LoProspect> {
  const { error } = await supabase.rpc('lo_prospect_set_preview_access', {
    p_prospect_id: prospectId,
    p_enabled: enabled,
    p_reason: reason ?? null
  });
  if (error) {
    throw error;
  }
  const refreshed = await getLoProspect(prospectId);
  if (!refreshed) {
    throw new Error('Prospect not found after access change.');
  }
  return refreshed;
}

/**
 * Clear an LO's preview-invite suppression and restore eligible NMLS access.
 * This intentionally does not send an email or revive previously revoked links.
 */
export async function restorePreviewInviteSubscription(prospectId: string): Promise<LoProspect> {
  const { data, error } = await supabase.rpc('lo_prospect_restore_preview_invite_subscription', {
    p_prospect_id: prospectId
  });
  if (error) {
    throw error;
  }
  const status = (data as { status?: string } | null)?.status;
  if (status === 'closed') {
    throw new Error('This prospect is closed and preview access cannot be restored.');
  }
  if (status === 'pending_review') {
    throw new Error('Approve this registration by sending its first invite instead.');
  }
  if (status !== 'ok' && status !== 'subscribed') {
    throw new Error('Preview invite access could not be restored.');
  }

  const refreshed = await getLoProspect(prospectId);
  if (!refreshed) {
    throw new Error('Prospect not found after restoring invite access.');
  }
  return refreshed;
}

// ============================================================================
// Pass / reopen (recruiter lifecycle actions)
// ============================================================================

/**
 * Where a reopened (previously passed) prospect lands. Self-registrations that
 * were never approved go back to review; anyone who priced returns to
 * previewing; otherwise back to invited.
 */
export function deriveReopenStage(p: LoProspect): LoProspectStage {
  if (p.source === 'self_registration' && p.registrationStatus !== 'approved') {
    return 'registered';
  }
  return p.runsUsed > 0 ? 'previewing' : 'invited';
}

/**
 * Mark a prospect as passed: cut preview access first (audit reason + token
 * revocation + reservation expiry via the access RPC — which refuses terminal
 * rows, so the disable must precede the stage write), then set the stage.
 * Partial failure (disabled but not passed) is benign: a retry skips the disable.
 */
export async function passLoProspect(p: LoProspect, userId: string, reason?: string): Promise<LoProspect> {
  if (p.stage === 'in_recruiting' || p.stage === 'passed') {
    throw new Error('Only an active prospect can be marked as passed.');
  }
  if (p.publicPreviewEnabled) {
    // The RPC NULLIFs blank reasons — fall back before the call or the audit is lost.
    await setPreviewAccess(p.id, false, reason?.trim() || 'Marked as passed');
  }
  return setLoProspectStage(p.id, 'passed', userId);
}

/**
 * Reopen a passed prospect back into the pipeline. Forces access off first:
 * legacy rows passed by the old stage-only action can still carry
 * publicPreviewEnabled=true (harmless while passed — terminal gates refuse),
 * and restoring a non-terminal stage would put the portal live with no fresh
 * invite. Disabling a still-passed row is allowed (the RPC only refuses
 * ENABLING terminal rows). Access stays off until a new invite is sent.
 */
export async function reopenLoProspect(p: LoProspect, userId: string): Promise<LoProspect> {
  if (p.stage !== 'passed') {
    throw new Error('Only a passed prospect can be reopened.');
  }
  if (p.publicPreviewEnabled) {
    await setPreviewAccess(p.id, false, 'Reopened prospect');
  }
  return setLoProspectStage(p.id, deriveReopenStage(p), userId);
}

// ============================================================================
// Pricing run (server-resolved OB identity, via edge function)
// ============================================================================

export interface LoProspectScenarioInput {
  loanPurpose: string; // OB enum, incl. 'FHAStreamlineRefi' / 'VARateReduction'
  loanType: string; // OB enum: 'Conventional' | 'FHA' | 'VA' | 'USDARuralHousing' | 'NonConforming'
  productTypes?: string[]; // default ['StandardProducts']
  loanAmount: number;
  appraisedValue: number;
  fico: number;
  dti?: number; // default 43 when omitted (OB requires the field — always sent)
  termYears: number;
  lockDays?: number; // default 30
  compPercent: number; // LO comp target, 0.00-3.50 -> desiredPrice 100+comp
  targetRate?: number; // reference-only note rate (no longer the OB search anchor)
  propertyState: string;
  propertyZip: string;
  propertyCounty: string;
  propertyType: string; // OB enum (full surface incl. non-warrantable/condotel/manufactured)
  occupancy: string; // OB enum
  cashOutAmount?: number;
  // ── extended scenario surface (all optional; presence-keyed — omitted keys
  //    reproduce the legacy payload exactly). Mirrors the edge functions'
  //    Scenario in supabase/functions/_shared/lo-prospect-ob-request.ts. ──────
  lienType?: string; // 'First' | 'Second'
  firstLienAmount?: number; // required when Second
  secondLienAmount?: number; // subordinate financing on a FIRST lien
  purchasePrice?: number; // real sales price (falls back to appraisedValue)
  amortizationType?: string; // 'Fixed' | 'ARM'
  armFixedTerm?: string; // OB ARMFixedTerm, required when ARM
  interestOnly?: boolean;
  interestOnlyTermMonths?: number; // 12–120
  aus?: string;
  buydown?: string;
  borrowerPaidMI?: string; // 'Yes' | 'No'
  reducedMI?: boolean;
  waiveEscrows?: boolean;
  selfEmployed?: boolean;
  firstTimeHomeBuyer?: boolean;
  citizenship?: string;
  monthsReserves?: number; // required with ExpandedGuidelines
  propertiesFinanced?: number;
  numberOfUnits?: string;
  numberOfStories?: number;
  prepaymentPenalty?: string; // investment occupancy only (OB returns 0 products otherwise)
  veteranType?: string; // required for VA on the public path; the modal requires it too
  incomeVerificationType?: string; // Artemis-proven 18 OB enums (12/24-mo bank stmts only)
  dscrRatio?: number; // required iff InvestorDscr
  housingEvent?: string;
  housingEventSeasoning?: string;
  bankruptcy?: string;
  bankruptcyOutcome?: string;
  bankruptcySeasoning?: string;
  mortgageLates?: string; // '0x30' | '1x30' | '2x30' | '1x60' | '1x90'
  debtConsolidation?: boolean;
  uniqueProperty?: boolean;
}

/**
 * Trigger a pricing run for a prospect through the server-resolved OB identity.
 * Capture is performed server-side by the lo-prospect-price edge function.
 */
export async function runLoProspectPricing(
  prospectId: string,
  scenario: LoProspectScenarioInput,
  clientRequestId?: string
): Promise<{ runId: string | null; status: string; message?: string; compFunded?: boolean }> {
  const { data, error } = await supabase.functions.invoke('lo-prospect-price', {
    body: { loProspectId: prospectId, scenario, clientRequestId }
  });
  if (error) {
    throw error;
  }
  return data as { runId: string | null; status: string; message?: string; compFunded?: boolean };
}

// ============================================================================
// Transfer into the Recruiting table
// ============================================================================

/**
 * Create a recruit at 'Inquiry' from a prospect and link it back, in a single
 * atomic, idempotent DB transaction (lo_prospect_transfer_to_recruit). Retrying
 * a transfer returns the existing recruit instead of creating a duplicate.
 * `formValues` must already satisfy recruitmentFormSchema (collected by the
 * Transfer dialog). recruitToDatabase builds the full insert; the RPC ignores
 * any client-supplied org and uses the prospect's own organization.
 */
export async function transferLoProspectToRecruit(
  prospectId: string,
  formValues: RecruitmentFormValues,
  organizationId: string,
  userId: string
): Promise<{ recruitId: string }> {
  const recruit = recruitToDatabase(formValues, organizationId, userId);
  const { data, error } = await supabase.rpc('lo_prospect_transfer_to_recruit', {
    p_prospect_id: prospectId,
    p_recruit: recruit
  });
  if (error) {
    throw error;
  }
  log.info('Transferred prospect to recruit', { prospectId, recruitId: data as string });
  return { recruitId: data as string };
}

export async function deleteLoProspect(prospectId: string): Promise<void> {
  const { error } = await supabase.from('lo_prospects').delete().eq('id', prospectId);
  if (error) {
    throw error;
  }
}
